import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Facebook, Twitter, Instagram, Mail } from 'lucide-react';
import './NewsArticle.css';
import usePageTitle from '../hooks/usePageTitle';
import {
  getNewsArticleById,
  getSortedNewsArticles,
  sortNewsByDateDesc,
} from '../data/newsArticles';

const NewsArticle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  const articles = getSortedNewsArticles();
  const article = getNewsArticleById(articleId) ?? articles[0];
  
  usePageTitle(`${article.title} - The Kults`);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = article.title;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
        break;
      case 'instagram':
        // Instagram doesn't support direct sharing via URL, so we'll copy to clipboard
        navigator.clipboard.writeText(`${text} ${url}`);
        alert('Link copied to clipboard! Share it on Instagram.');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}\n\nRead more: ${url}`)}`);
        break;
    }
  };

  return (
    <div className="news-article-page">
      {/* Back Navigation */}
      <div className="article-back-nav">
        <div className="container">
          <Link to="/news" className="back-link">
            <ArrowLeft size={20} />
            Back to News
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="container">
          <div className="article-meta">
            <span className="article-category">
              <Tag size={16} />
              {article.category}
            </span>
            <span className="article-date">
              <Calendar size={16} />
              {article.date}
            </span>
            <span className="article-author">
              <User size={16} />
              {article.author}
            </span>
            <span className="article-read-time">{article.readTime}</span>
          </div>
          
          <h1 className="article-title">{article.title}</h1>
          <p className="article-excerpt">{article.excerpt}</p>
          
          <div className="article-tags">
            {article.tags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
        </div>
      </header>

      {/* Article Image */}
      <div className="article-image-section">
        <div className="container">
          <div className="article-image">
            <img src={article.image} alt={article.title} />
          </div>
        </div>
      </div>

      {/* Article Content */}
      <main className="article-content">
        <div className="container">
          <div className="content-wrapper">
            <article className="article-body">
              <div 
                className="article-text"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>
            
            {/* Sidebar */}
            <aside className="article-sidebar">
              <div className="share-section">
                <h3>Share This Article</h3>
                <div className="share-buttons">
                  <button 
                    onClick={() => handleShare('facebook')}
                    className="share-btn facebook"
                    aria-label="Share on Facebook"
                  >
                    <Facebook size={20} />
                    Facebook
                  </button>
                  <button 
                    onClick={() => handleShare('twitter')}
                    className="share-btn twitter"
                    aria-label="Share on Twitter"
                  >
                    <Twitter size={20} />
                    Twitter
                  </button>
                  <button 
                    onClick={() => handleShare('instagram')}
                    className="share-btn instagram"
                    aria-label="Share on Instagram"
                  >
                    <Instagram size={20} />
                    Instagram
                  </button>
                  <button 
                    onClick={() => handleShare('email')}
                    className="share-btn email"
                    aria-label="Share via Email"
                  >
                    <Mail size={20} />
                    Email
                  </button>
                </div>
              </div>
              
              <div className="related-content">
                <h3>Related Articles</h3>
                <div className="related-articles">
                  {sortNewsByDateDesc(articles.filter((a) => a.id !== articleId)).map(
                    (related) => (
                    <Link key={related.id} to={`/news/${related.id}`} className="related-article">
                      <div className="related-image">
                        <img src={related.image} alt={related.title} />
                      </div>
                      <div className="related-info">
                        <h4>{related.title}</h4>
                        <span className="related-date">{related.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Call to Action */}
      <section className="article-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Stay Connected</h2>
            <p>Get the latest news, tour updates, and exclusive content delivered to your inbox.</p>
            <div className="cta-buttons">
              <Link to="/news" className="btn btn-primary">
                View All News
              </Link>
              <Link to="/store" className="btn btn-secondary">
                Visit Store
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsArticle; 